/** Presentation-only helpers. Nothing here decides what is fetched, only how it reads. */

const NUMBER = new Intl.NumberFormat();

const COMPACT = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

/** Timestamps are stored and travel as UTC, and are shown in the browser's zone. */
const TIMESTAMP = new Intl.DateTimeFormat(undefined, {
   year: 'numeric', month: '2-digit', day: '2-digit',
   hour: '2-digit', minute: '2-digit', second: '2-digit',
   hour12: false
});

const DATE = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });

const TIME_ONLY = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });

const DAY_ONLY = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

export function number(value)
{
   return typeof value === 'number' ? NUMBER.format(value) : String(value ?? '');
}

export function compact(value)
{
   return typeof value === 'number' ? COMPACT.format(value) : String(value ?? '');
}

export function timestamp(value)
{
   const date = new Date(value);
   return Number.isNaN(date.getTime()) ? String(value) : TIMESTAMP.format(date);
}

/** The day alone, for a due date, where the hour and minute would be noise nobody set. */
export function dateOnly(value)
{
   const date = new Date(value);
   return Number.isNaN(date.getTime()) ? String(value) : DATE.format(date);
}

/** Bucket labels: the time alone within a day, the date once the range spans days. */
export function bucketLabel(value, spanMs)
{
   const date = new Date(value);
   if (Number.isNaN(date.getTime()))
   {
      return String(value);
   }

   return spanMs > 36 * 3600 * 1000 ? DAY_ONLY.format(date) : TIME_ONLY.format(date);
}

export function duration(milliseconds)
{
   const seconds = Math.round(milliseconds / 1000);
   if (seconds < 60)
   {
      return `${seconds}s`;
   }

   const minutes = Math.round(seconds / 60);
   if (minutes < 60)
   {
      return `${minutes}m`;
   }

   const hours = Math.round(minutes / 60);
   return hours < 48 ? `${hours}h` : `${Math.round(hours / 24)}d`;
}

/**
 * The same span written out, for a sentence rather than for a column.
 *
 * `24h` is right in a cell, where the column heading has already said what the number is, and wrong in
 * "changed 24h ago" - which is read aloud. The unit is the largest that does not round the value away.
 */
export function durationWords(milliseconds)
{
   const units = [['day', 36 * 3_600_000], ['hour', 3_600_000], ['minute', 60_000], ['second', 0]];
   const sizes = { day: 86_400_000, hour: 3_600_000, minute: 60_000, second: 1000 };
   const [name] = units.find(([, floor]) => milliseconds >= floor);
   const count = Math.max(1, Math.round(milliseconds / sizes[name]));

   return `${count} ${name}${count === 1 ? '' : 's'}`;
}

/**
 * How long ago something happened, as a change log reads it.
 *
 * The absolute instant is what a person quotes and the relative one is what they scan, so a timeline
 * shows this beside {@link timestamp} rather than instead of it. A future instant - a due date - reads
 * as "in 3 days" rather than as a negative age.
 */
export function ago(value, now = Date.now())
{
   const at = new Date(value).getTime();

   if (Number.isNaN(at))
   {
      return '';
   }

   const elapsed = now - at;

   if (Math.abs(elapsed) < 60_000)
   {
      return 'just now';
   }

   return elapsed >= 0 ? `${durationWords(elapsed)} ago` : `in ${durationWords(-elapsed)}`;
}

/**
 * A field name as a person reads it: `DueAt` becomes "Due at", `created_at` becomes "Created at".
 *
 * The name itself stays visible wherever there is room for it - a column's help, a form field's
 * description - because it is what a filter, a shared link and the API all spell, and a reader who
 * only ever sees "Due at" cannot find `DueAt` in Explore's field list. This is the label, not a rename.
 */
export function fieldLabel(name)
{
   const words = String(name ?? '')
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .trim();

   if (words === '')
   {
      return '';
   }

   return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * An enum member as a person reads it: `in_progress` becomes "In progress".
 *
 * The API's vocabulary is snake_case throughout, and it is what a filter and a link carry - so this is
 * the same trade {@link fieldLabel} makes. Never store or send what this returns.
 */
export function enumLabel(value)
{
   const words = String(value ?? '').replace(/[_-]+/g, ' ').trim();

   return words === '' ? '' : words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Renders a value out of a result row.
 *
 * Everything arrives as JSON, so a column may hold a number, an ISO instant, a null, or free text of
 * any length. `dataType` is the field's declared type from `GET /api/v1/sourcetypes/{key}`, which is
 * what tells an instant from a string - there is no field-name convention to fall back on here, and
 * guessing from the shape of a string would turn a title that happens to parse as a date into one.
 */
export function cell(value, dataType)
{
   if (value === null || value === undefined)
   {
      return '';
   }

   if (dataType === 'timestamp')
   {
      return timestamp(value);
   }

   if (dataType === 'enum')
   {
      return enumLabel(value);
   }

   if (typeof value === 'number')
   {
      return number(value);
   }

   if (typeof value === 'boolean')
   {
      return value ? 'true' : 'false';
   }

   return String(value);
}
